import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

function _deviceId() {
  let id = localStorage.getItem("pv_device_id");
  if (!id) {
    id = (crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now().toString(36))).slice(0, 40);
    localStorage.setItem("pv_device_id", id);
  }
  return id;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pv_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pv_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("pv_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("pv_token");
        localStorage.removeItem("pv_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Password login. Ritorna oggetto { mfa_required, mfa_challenge_id, user, access_token }.
  // Se mfa_required=false: sessione completa attivata direttamente.
  // Se mfa_required=true: chiamante deve invocare `verifyMfa` con il codice.
  const login = async (email, password, turnstileToken) => {
    const payload = { email, password };
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const { data } = await api.post("/auth/login", payload, {
      headers: { "x-device-id": _deviceId() },
    });
    if (data.mfa_required) {
      // Salva token pending, ma NON impostare user autenticato finché MFA non è OK
      localStorage.setItem("pv_token", data.access_token);
      return {
        mfa_required: true,
        mfa_challenge_id: data.mfa_challenge_id,
        pending_user: data.user,
        phone_verified: data.phone_verified,
      };
    }
    localStorage.setItem("pv_token", data.access_token);
    localStorage.setItem("pv_user", JSON.stringify(data.user));
    setUser(data.user);
    return { mfa_required: false, user: data.user };
  };

  const verifyMfa = async (challengeId, code, trustDevice) => {
    const { data } = await api.post("/auth/mfa/verify",
      { challenge_id: challengeId, code, trust_device: !!trustDevice },
      { headers: { "x-device-id": _deviceId() } });
    if (data.access_token) {
      localStorage.setItem("pv_token", data.access_token);
    }
    // Fetch user aggiornato
    const me = await api.get("/auth/me");
    localStorage.setItem("pv_user", JSON.stringify(me.data));
    setUser(me.data);
    return me.data;
  };

  const requestPasswordless = async (email) => {
    const { data } = await api.post("/auth/otp/request-passwordless", { email });
    return data;
  };

  const verifyPasswordless = async (email, code) => {
    const { data } = await api.post("/auth/otp/verify-passwordless", { email, code });
    localStorage.setItem("pv_token", data.access_token);
    localStorage.setItem("pv_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("pv_token");
    localStorage.removeItem("pv_user");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setUser,
                               verifyMfa, requestPasswordless, verifyPasswordless,
                               deviceId: _deviceId() }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
